import { useApi } from '@hooks/useApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import { useState } from 'react';
import { isError } from '@utils/apiHelpers';
import { UseFormSetError } from 'react-hook-form';
import { AmountData, OrderActionResp } from './postRefund';

export type CaptureData = AmountData & {
  final: boolean;
};

export const usePostOrderCapture = ({
  id,
  callback,
  setError,
}: {
  id: string;
  callback?: () => void;
  setError: UseFormSetError<{
    amount: number;
    final: boolean;
  }>;
}) => {
  const { query } = useApi();
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<string>('');

  const { mutate, isPending } = useMutation({
    mutationFn: (captureData: CaptureData): Promise<OrderActionResp> => {
      return query({
        path: `v1/orders/${id}/capture`,
        data: captureData,
        opt: {
          method: 'POST',
        },
      });
    },
    onSettled: (data) => {
      if (!data) return;
      if (isError(data)) {
        setApiError(data.message);
        if (data.errors) {
          Object.keys(data.errors).forEach((err) => {
            if (!data.errors) return;
            setError(err as 'amount' | 'final' | 'root', {
              type: 'manual',
              message: data.errors[err].join(', '),
            });
          });
        }
      }
    },
    onError: (error) => {
      setApiError(error.message);
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: [...queryKeys.order, id] });
      if (callback && !isError(data)) callback();
    },
  });

  return {
    mutate,
    isPending,
    error: apiError,
  };
};
